import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

export type ReblogType = 'reblog' | 'quote' | 'remix';

export interface ReblogRecord {
  id: string;
  post_id: string;
  user_id: string;
  type: ReblogType;
  content?: string | null;
  parent_id?: string | null;
  created_at: string;
}

export interface ProfileLite {
  id: string;
  username: string;
  display_name: string;
  profile_image_url?: string | null;
}

export interface ReblogAnnotation {
  rebloggedBy?: ProfileLite; // someone you follow who reblogged
  quoteChips: Array<{ id: string; content: string; user?: ProfileLite; created_at: string }>; // up to 2
}

export function useReblogActions() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Track which posts the current user has reblogged
  const { data: userReblogs } = useQuery({
    queryKey: ['user-reblogs', user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      if (!user?.id) return new Map();
      
      try {
        const { data, error } = await supabase
          .from('loops_reblogs')
          .select('id, post_id, type')
          .eq('user_id', user.id);
        
        if (error) {
          // If table doesn't exist yet, return empty map
          if (error.message.includes('does not exist')) {
            return new Map();
          }
          throw error;
        }
        
        // Create a map of post_id -> { reblog_id, type } for quick lookup
        const reblogsMap = new Map();
        (data || []).forEach((reblog) => {
          reblogsMap.set(reblog.post_id, { reblog_id: reblog.id, type: reblog.type });
        });
        
        return reblogsMap;
      } catch (err) {
        console.error('[useReblogActions] Error fetching user reblogs:', err);
        return new Map();
      }
    },
  });
  
  // Check if a post has been reblogged by current user
  const hasReblogged = (postId: string) => {
    return userReblogs?.has(postId);
  };
  
  // Get reblog details for a post
  const getReblogDetails = (postId: string) => {
    return userReblogs?.get(postId);
  };

  // Mutation to create a reblog
  const mutateReblog = useMutation({
    mutationFn: async ({ postId, type, content, parentId }: { postId: string; type: ReblogType; content?: string; parentId?: string | null; }) => {
      if (!user?.id) throw new Error('Authentication required');

      // Preflight: verify table exists and SELECT works (policy allows SELECT for everyone per migration)
      try {
        const pre = await supabase
          .from('loops_reblogs')
          .select('id', { count: 'exact', head: true });
        if (pre.error) {
          // If relation missing or other structural error, surface a clearer message
          const code = (pre.error as any).code;
          const msg = (pre.error.message || '').toLowerCase();
          if (code === '42P01' || msg.includes('does not exist')) {
            throw Object.assign(new Error('Re-share unavailable (table missing)'), {
              code: '42P01',
              details: 'loops_reblogs missing in this environment',
            });
          }
        }
      } catch (pfErr) {
        // Re-throw to be handled by onError mapping below
        throw pfErr;
      }
      const payload: any = {
        post_id: postId,
        user_id: user.id,
        type,
      };
      if (content) payload.content = content;
      if (parentId) payload.parent_id = parentId;
      const { error } = await supabase.from('loops_reblogs').insert(payload);
      if (error) {
        // Surface more helpful errors to the UI by throwing with enriched info
        const enriched = new Error(error.message) as any;
        enriched.code = (error as any).code;
        enriched.details = (error as any).details;
        enriched.hint = (error as any).hint;
        throw enriched;
      }

      // Create notification for the post author (only for simple reblogs, not quotes/remixes)
      if (type === 'reblog') {
        try {
          const { data: loopData } = await supabase
            .from('loops')
            .select('user_id, image_url, thumbnail_url')
            .eq('id', postId)
            .single();

          if (loopData && loopData.user_id !== user.id) {
            await supabase
              .from('notifications')
              .insert({
                user_id: loopData.user_id,
                actor_id: user.id,
                type: 'reloop',
                loop_id: postId,
                image_url: loopData.thumbnail_url || loopData.image_url,
              });
          }
        } catch (notificationError) {
          console.error('Error creating reloop notification:', notificationError);
          // Don't fail the reblog if notification fails
        }
      }
    },
    onSuccess: (_res, variables) => {
      toast({ title: variables.type === 'reblog' ? 'post has been relooped!' : variables.type === 'quote' ? 'Quoted!' : 'Remixed!' });
      queryClient.invalidateQueries({ queryKey: ['loops'] });
      queryClient.invalidateQueries({ queryKey: ['reblog-annotations'] });
      queryClient.invalidateQueries({ queryKey: ['reblog-trail', variables.postId] });
    },
    onError: (err: any) => {
      // Map common cases to clearer messages
      const code = err?.code;
      const msg = (err?.message || '').toLowerCase();
      const details = err?.details || '';
      console.error('[useReblogActions] error', { code, message: err?.message, details: err?.details, hint: err?.hint });

      // Duplicate pure reblog (unique index)
      if (code === '23505' || msg.includes('duplicate key')) {
        toast({ title: 'Already reshared', description: 'You have already reshared this post.', variant: 'default' });
        return;
      }

      // RLS / permission errors
      if (code === '42501' || msg.includes('permission denied') || msg.includes('not allowed')) {
        toast({ title: 'Permission denied', description: 'You do not have permission to re-share this post.', variant: 'destructive' });
        return;
      }

      // Table missing (migration not applied yet)
      if (msg.includes('loops_reblogs') && (msg.includes('does not exist') || code === '42P01')) {
        toast({ title: 'Re-share unavailable', description: 'Re-sharing is not available yet. Please try again later.', variant: 'destructive' });
        return;
      }

      // FK constraint (invalid/expired post)
      if (code === '23503' || details?.toLowerCase().includes('is not present in table')) {
        toast({ title: 'Post not found', description: 'This post can no longer be reshared.', variant: 'destructive' });
        return;
      }

      // Fallback
      const fallbackDesc = (msg?.length === 0) 
        ? 'Unknown error. Try a hard refresh. If it persists, migrations may not be applied.' 
        : 'Please try again';
      toast({ title: 'Failed to re-share', description: fallbackDesc, variant: 'destructive' });
    }
  });

  // Mutation to delete a reblog (unreloop)
  const mutateUnreblog = useMutation({
    mutationFn: async ({ reblogId }: { reblogId: string }) => {
      if (!user?.id) throw new Error('Authentication required');
      
      // Delete the reblog entry
      const { error } = await supabase
        .from('loops_reblogs')
        .delete()
        .eq('id', reblogId)
        .eq('user_id', user.id); // Ensure user can only delete their own reblogs
      
      if (error) {
        // Surface helpful errors
        const enriched = new Error(error.message) as any;
        enriched.code = (error as any).code;
        enriched.details = (error as any).details;
        throw enriched;
      }
    },
    onSuccess: () => {
      toast({ title: 'Removed from your profile' });
      queryClient.invalidateQueries({ queryKey: ['loops'] });
      queryClient.invalidateQueries({ queryKey: ['user-reblogs'] });
      queryClient.invalidateQueries({ queryKey: ['reblog-annotations'] });
      queryClient.invalidateQueries({ queryKey: ['user-loops-and-reblogs'] }); // Update profile page
    },
    onError: (err: any) => {
      console.error('[useReblogActions] unreloop error:', err);
      toast({ title: 'Failed to remove repost', description: 'Please try again', variant: 'destructive' });
    }
  });

  return {
    reblog: (postId: string) => mutateReblog.mutate({ postId, type: 'reblog' }),
    quote: (postId: string, content: string) => mutateReblog.mutate({ postId, type: 'quote', content }),
    remix: (postId: string, content?: string) => mutateReblog.mutate({ postId, type: 'remix', content }),
    unreloop: (reblogId: string) => mutateUnreblog.mutate({ reblogId }),
    isReblogging: mutateReblog.isPending,
    isUnreblogging: mutateUnreblog.isPending,
    hasReblogged,
    getReblogDetails,
  };
}

export function useReblogAnnotations(postIds: string[] | undefined) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['reblog-annotations', user?.id, (postIds || []).join(',')],
    enabled: !!postIds && postIds.length > 0,
    queryFn: async (): Promise<Record<string, ReblogAnnotation>> => {
      const ids = postIds as string[];
      // 1) favorites as following set
      let followingIds: string[] = [];
      if (user?.id) {
        try {
          const { data: favs, error: favErr } = await supabase
            .from('user_favorites')
            .select('favorite_user_id')
            .eq('user_id', user.id);
          if (favErr) console.warn('[useReblogAnnotations] favorites error', favErr);
          followingIds = (favs || []).map((f: any) => f.favorite_user_id).filter(Boolean);
        } catch (e) {
          console.warn('[useReblogAnnotations] user_favorites unavailable', e);
        }
      }

      // 2) reblogs by followed users
      let followedReblogs: ReblogRecord[] = [];
      try {
        if (followingIds.length) {
          const { data, error } = await supabase
            .from('loops_reblogs')
            .select('*')
            .in('post_id', ids)
            .in('user_id', followingIds)
            .order('created_at', { ascending: false })
            .limit(200);
          if (!error && data) followedReblogs = data as any;
        }
      } catch (e) {
        // Table might not exist yet in some environments; return empty annotations
        console.warn('[useReblogAnnotations] loops_reblogs table unavailable (followed)', e);
      }

      // 3) quote chips: latest quotes across posts (we'll cap and group client-side)
      let quoteRecords: ReblogRecord[] = [];
      try {
        const { data, error } = await supabase
          .from('loops_reblogs')
          .select('*')
          .in('post_id', ids)
          .eq('type', 'quote')
          .order('created_at', { ascending: false })
          .limit(200);
        if (!error && data) quoteRecords = data as any;
      } catch (e) {
        console.warn('[useReblogAnnotations] loops_reblogs table unavailable (quotes)', e);
      }

      // Collect unique profile ids we need
      const profileIds = Array.from(new Set([
        ...followedReblogs.map(r => r.user_id),
        ...quoteRecords.map(r => r.user_id),
      ]));

      const profilesMap = new Map<string, ProfileLite>();
      if (profileIds.length) {
        const { data: profiles, error: pErr } = await supabase
          .from('public_profiles')
          .select('id, username, display_name, profile_image_url')
          .in('id', profileIds);
        if (!pErr && profiles) {
          for (const p of profiles as any[]) {
            profilesMap.set(p.id, {
              id: p.id,
              username: p.username,
              display_name: p.display_name,
              profile_image_url: p.profile_image_url,
            });
          }
        }
      }

      // Build annotations map
      const result: Record<string, ReblogAnnotation> = {};
      for (const id of ids) {
        result[id] = { quoteChips: [] };
      }

      // For each post, pick the most recent followed reblogger if exists
      for (const r of followedReblogs) {
        const current = result[r.post_id];
        if (current && !current.rebloggedBy) {
          const u = profilesMap.get(r.user_id);
          if (u) current.rebloggedBy = u;
        }
      }

      // For quotes, take up to 2 latest per post
      const quotesByPost: Record<string, ReblogRecord[]> = {};
      for (const r of quoteRecords) {
        if (!quotesByPost[r.post_id]) quotesByPost[r.post_id] = [];
        if (quotesByPost[r.post_id].length < 4) quotesByPost[r.post_id].push(r); // keep a few for local filtering
      }
      for (const [pid, list] of Object.entries(quotesByPost)) {
        const chips = list
          .sort((a, b) => (a.created_at < b.created_at ? 1 : -1))
          .slice(0, 2)
          .map(q => ({
            id: q.id,
            content: q.content || '',
            user: profilesMap.get(q.user_id),
            created_at: q.created_at,
          }));
        if (result[pid]) result[pid].quoteChips = chips;
      }

      return result;
    },
    staleTime: 60_000,
  });
}

export function useTrail(postId?: string) {
  return useQuery({
    queryKey: ['reblog-trail', postId],
    enabled: !!postId,
    queryFn: async () => {
      try {
        if (!postId) return [] as Array<ReblogRecord & { user?: ProfileLite }>;
        const { data, error } = await supabase
          .from('loops_reblogs')
          .select('*')
          .eq('post_id', postId)
          .order('created_at', { ascending: true });
        if (error) throw error;
        const recs = (data || []) as ReblogRecord[];
        const ids = Array.from(new Set(recs.map(r => r.user_id)));
        let profilesMap = new Map<string, ProfileLite>();
        if (ids.length) {
          const { data: profiles, error: pErr } = await supabase
            .from('public_profiles')
            .select('id, username, display_name, profile_image_url')
            .in('id', ids);
          if (!pErr && profiles) {
            for (const p of profiles as any[]) {
              profilesMap.set(p.id, {
                id: p.id,
                username: p.username,
                display_name: p.display_name,
                profile_image_url: p.profile_image_url,
              });
            }
          }
        }
        return recs.map(r => ({ ...r, user: profilesMap.get(r.user_id) }));
      } catch (e) {
        console.warn('[useTrail] loops_reblogs table unavailable', e);
        return [] as Array<ReblogRecord & { user?: ProfileLite }>;
      }
    },
  });
}
